import {
    AlertTriangle,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Clock,
    LoaderCircle,
    RefreshCw,
    TrendingDown,
    TrendingUp,
    Wallet,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './YearEndLeaveProcessScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const formatAmount = (amount) => {
    const value = toNumber(amount, 0)
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const YearEndLeaveProcessScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [year, setYear] = useState(new Date().getFullYear())
    const [projection, setProjection] = useState(null)
    const [isLoadingDryRun, setIsLoadingDryRun] = useState(false)
    const [isExecuting, setIsExecuting] = useState(false)
    const [isExecuted, setIsExecuted] = useState(false)
    const [expandedStaff, setExpandedStaff] = useState(new Set())

    const currentYear = new Date().getFullYear()
    const canGoPrevYear = year > 2020
    const canGoNextYear = year < currentYear + 1

    const runDryRun = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setIsLoadingDryRun(false)
            return
        }

        try {
            setIsLoadingDryRun(true)
            setProjection(null)

            const response = await fetch(BASE_URL_API + 'runYearEndLeaveProcess', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    year,
                    dryRun: true,
                }),
            })

            if (!response.ok) {
                toast.error('Failed to load year-end projection.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                setProjection(respo.response)
                if (respo.response?.alreadyRan) {
                    toast.warning(`Year-end process already run for ${year}.`)
                }
            } else {
                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }
                handleError(respo)
            }
        } catch (error) {
            console.error('runDryRun error:', error)
            toast.error('Network error.')
        } finally {
            setIsLoadingDryRun(false)
        }
    }, [authCtx, activeStore?.storeIdentifier, year])

    useEffect(() => {
        runDryRun()
    }, [runDryRun])

    const executeYearEnd = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !projection) {
            toast.error('Missing required data.')
            return
        }

        const proceed = window.confirm(
            `Execute Year-End Process?\n\nThis will apply carry-forward, lapse, and encashment rules for all ${projection?.totalStaffProcessed} staff for ${year}. This action cannot be undone.`
        )

        if (!proceed) return

        try {
            setIsExecuting(true)

            const response = await fetch(BASE_URL_API + 'runYearEndLeaveProcess', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    year,
                    dryRun: false,
                    createdBy: authCtx?.authIdentifier || '',
                }),
            })

            if (!response.ok) {
                toast.error('Execution failed.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                setIsExecuted(true)
                setProjection(respo.response)
                toast.success(
                    `Year-end complete. ${respo.response?.nextYear || year + 1} opening balances created.`
                )
            } else {
                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }
                handleError(respo)
            }
        } catch (error) {
            console.error('executeYearEnd error:', error)
            toast.error('Network error.')
        } finally {
            setIsExecuting(false)
        }
    }

    const handleError = (respo) => {
        switch (respo?.errorCode) {
            case 'LEAVE_004':
                toast.warning(`Year-end process already completed for ${year}.`)
                break
            case 'LEAVE_002':
                toast.error('No leave policies configured. Set up policies first.')
                navigate('/payroll/setup/leave-policy')
                break
            case 'LEAVE_001':
                toast.error('No leave balances found. Initialize balances first.')
                navigate('/payroll/setup/leave-balance-init')
                break
            case 'VAL_002':
                toast.error(respo?.message || 'Invalid year selected.')
                break
            case 'SYS_002':
                toast.error(respo?.message || 'Process failed. All changes rolled back.')
                break
            default:
                toast.error(respo?.message || 'Process failed.')
        }
    }

    const summary = useMemo(() => projection?.summary || {}, [projection])
    const perStaff = useMemo(() => Array.isArray(projection?.perStaff) ? projection.perStaff : [], [projection])

    const toggleStaffExpand = (staffId) => {
        const next = new Set(expandedStaff)
        if (next.has(staffId)) {
            next.delete(staffId)
        } else {
            next.add(staffId)
        }
        setExpandedStaff(next)
    }

    if (!activeStore?.storeIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store first.</strong>
                        <p>Year-end processing is store-specific.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Year-End Leave Processing</h1>
                    <p className={styles.headerSub}>Apply carry-forward, lapse, and encashment rules</p>
                </div>
            </div>

            <div className={styles.yearSelector}>
                <button
                    type="button"
                    className={styles.yearArrowBtn}
                    onClick={() => setYear((prev) => prev - 1)}
                    disabled={!canGoPrevYear || isLoadingDryRun}
                >
                    <ChevronLeft size={16} />
                </button>
                <span className={styles.yearLabel}>{year}</span>
                <button
                    type="button"
                    className={styles.yearArrowBtn}
                    onClick={() => setYear((prev) => prev + 1)}
                    disabled={!canGoNextYear || isLoadingDryRun}
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            {isLoadingDryRun ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={18} className={styles.spinIcon} />
                    <span>Loading year-end projection...</span>
                </div>
            ) : !projection ? (
                <div className={styles.errorCard}>
                    <AlertTriangle size={16} />
                    <span>Unable to load projection. Please try again.</span>
                </div>
            ) : (
                <>
                    {projection?.alreadyRan && (
                        <div className={styles.alreadyRanBanner}>
                            <AlertTriangle size={16} />
                            <span>Year-end process has already been run for {year}. Execute button disabled.</span>
                        </div>
                    )}

                    <div className={styles.summaryCard}>
                        <div className={styles.summaryHead}>
                            <span className={styles.staffCount}>
                                {toNumber(projection?.totalStaffProcessed, 0)} staff affected
                            </span>
                        </div>

                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryRow}>
                                <div className={styles.summaryItem}>
                                    <TrendingUp size={14} className={styles.summaryIconCarry} />
                                    <span className={styles.summaryLabel}>PL Carry Forward</span>
                                    <span className={styles.summaryValue}>
                                        {toNumber(summary?.totalPLCarriedForward, 0).toFixed(1)}d
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <TrendingDown size={14} className={styles.summaryIconLapse} />
                                    <span className={styles.summaryLabel}>PL Lapsed</span>
                                    <span className={styles.summaryValue}>
                                        {toNumber(summary?.totalPLLapsed, 0).toFixed(1)}d
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <Wallet size={14} className={styles.summaryIconEncash} />
                                    <span className={styles.summaryLabel}>PL Encashed</span>
                                    <span className={styles.summaryValue}>
                                        {toNumber(summary?.totalPLToBeEncashed, 0).toFixed(1)}d
                                    </span>
                                </div>
                            </div>

                            <div className={styles.summaryRow}>
                                <div className={styles.summaryItem}>
                                    <TrendingDown size={14} className={styles.summaryIconLapse} />
                                    <span className={styles.summaryLabel}>CL Lapsed</span>
                                    <span className={styles.summaryValue}>
                                        {toNumber(summary?.totalCLLapsed, 0).toFixed(1)}d
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <TrendingDown size={14} className={styles.summaryIconLapse} />
                                    <span className={styles.summaryLabel}>SL Lapsed</span>
                                    <span className={styles.summaryValue}>
                                        {toNumber(summary?.totalSLLapsed, 0).toFixed(1)}d
                                    </span>
                                </div>
                            </div>
                        </div>

                        {toNumber(summary?.estimatedEncashmentPayout, 0) > 0 && (
                            <div className={styles.liabilityRow}>
                                <span className={styles.liabilityLabel}>Est. Encashment Payout</span>
                                <span className={styles.liabilityAmount}>
                                    ₹{formatAmount(summary?.estimatedEncashmentPayout)}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={styles.breakdownCard}>
                        <div className={styles.breakdownHead}>Per-Staff Breakdown</div>
                        <div className={styles.staffList}>
                            {perStaff.map((staff) => {
                                const isExpanded = expandedStaff.has(staff?.staffIdentifier)
                                return (
                                    <div key={staff?.staffIdentifier} className={styles.staffRow}>
                                        <button
                                            type="button"
                                            className={styles.staffRowBtn}
                                            onClick={() => toggleStaffExpand(staff?.staffIdentifier)}
                                        >
                                            {isExpanded ? (
                                                <ChevronUp size={16} />
                                            ) : (
                                                <ChevronDown size={16} />
                                            )}
                                            <span className={styles.staffName}>{staff?.staffName || 'Staff'}</span>
                                        </button>

                                        {isExpanded && (
                                            <div className={styles.staffDetails}>
                                                {Array.isArray(staff?.leaveTypes) && staff.leaveTypes.map((leave) => (
                                                    <div key={leave?.leaveType} className={styles.leaveRow}>
                                                        <span className={styles.leaveTypeLabel}>
                                                            {leave?.leaveType}
                                                        </span>
                                                        <div className={styles.leaveBreakdown}>
                                                            {toNumber(leave?.carryDays, 0) > 0 && (
                                                                <span className={styles.leaveItem}>
                                                                    <TrendingUp size={12} className={styles.carryIcon} />
                                                                    {toNumber(leave?.carryDays, 0).toFixed(1)}d carry
                                                                </span>
                                                            )}
                                                            {toNumber(leave?.encashDays, 0) > 0 && (
                                                                <span className={styles.leaveItem}>
                                                                    <Wallet size={12} className={styles.encashIcon} />
                                                                    {toNumber(leave?.encashDays, 0).toFixed(1)}d encash (₹
                                                                    {formatAmount(leave?.encashPayoutEstimate)})
                                                                </span>
                                                            )}
                                                            {toNumber(leave?.lapseDays, 0) > 0 && (
                                                                <span className={styles.leaveItem}>
                                                                    <TrendingDown size={12} className={styles.lapseIcon} />
                                                                    {toNumber(leave?.lapseDays, 0).toFixed(1)}d lapses
                                                                </span>
                                                            )}
                                                            {toNumber(leave?.carryDays, 0) === 0 &&
                                                                toNumber(leave?.encashDays, 0) === 0 &&
                                                                toNumber(leave?.lapseDays, 0) === 0 && (
                                                                <span className={styles.leaveItem}>— No change</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {isExecuted ? (
                        <div className={styles.doneCard}>
                            <CheckCircle2 size={24} className={styles.doneIcon} />
                            <div>
                                <p className={styles.doneTitle}>
                                    Year-end complete. {projection?.nextYear || year + 1} opening balances created.
                                </p>
                                <p className={styles.doneSub}>All carry-forward, lapse, and encashment transactions recorded.</p>
                            </div>
                            <button
                                type="button"
                                className={styles.doneBtn}
                                onClick={() =>
                                    navigate('/payroll/leave-balance-overview', {
                                        state: { preferredYear: projection?.nextYear || year + 1 },
                                    })
                                }
                            >
                                View {projection?.nextYear || year + 1} Balances <ArrowRight size={14} />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={styles.executeBtn}
                            onClick={executeYearEnd}
                            disabled={isExecuting || projection?.alreadyRan}
                        >
                            {isExecuting ? (
                                <>
                                    <LoaderCircle size={16} className={styles.spinIcon} /> Executing...
                                </>
                            ) : (
                                <>
                                    Execute Year-End Process <ArrowRight size={15} />
                                </>
                            )}
                        </button>
                    )}
                </>
            )}
        </div>
    )
}

export default YearEndLeaveProcessScreen